<template>
  <main class="auth-page">
    <section class="auth-card" aria-labelledby="login-title">
      <aside class="auth-brand" aria-label="VTC LD Writing System">
        <div class="auth-brand__lockup">
          <span class="auth-brand__institution">VTC LD</span>
          <span class="auth-brand__name">Writing System</span>
          <span class="auth-brand__descriptor">Academic writing support</span>
        </div>
        <p class="auth-brand__note">A focused space for planning, drafting, and reviewing academic work.</p>
      </aside>

      <div class="auth-panel">
        <div class="auth-kicker">Secure access</div>
        <h1 id="login-title">AI Writing Assistant</h1>
        <p class="auth-copy">Sign in with your assigned account to continue.</p>

        <form v-if="!resetMode" class="auth-form" :aria-busy="loading" @submit.prevent="submitLogin">
          <label class="auth-field form-field">
            <span class="form-field__label">Account type</span>
            <select v-model="role" class="ui-select" name="role">
              <option value="student">Student</option>
              <option value="teacher">Teacher</option>
              <option value="admin">Administrator</option>
            </select>
          </label>

          <label class="auth-field form-field">
            <span class="form-field__label">Username</span>
            <input
              v-model="username"
              class="ui-input"
              name="username"
              autocomplete="username"
              placeholder="Enter username"
            />
          </label>

          <label class="auth-field form-field">
            <span class="form-field__label">Password</span>
            <input
              v-model="password"
              class="ui-input"
              name="password"
              type="password"
              autocomplete="current-password"
              placeholder="Enter password"
            />
          </label>

          <div v-if="errorMessage" class="auth-error ui-notice" role="alert">{{ errorMessage }}</div>
          <p class="sr-only" aria-live="polite">{{ loading ? 'Logging in...' : '' }}</p>

          <button class="btn-primary auth-submit" type="submit" :disabled="loading">
            {{ loading ? 'Logging in...' : 'Log in' }}
          </button>
          <button class="forgot-link" type="button" @click="openReset">Forgot password?</button>
        </form>

        <form v-else class="auth-form" :aria-busy="resetLoading" @submit.prevent="submitResetRequest">
          <router-link to="/account/reset-password">Reset using my verified email</router-link>
          <h2>Ask a teacher for help</h2>
          <p class="auth-copy">Enter the assigned username. A teacher or administrator will issue a temporary password if the account can be verified.</p>
          <label class="auth-field form-field">
            <span class="form-field__label">Username</span>
            <input v-model="resetUsername" class="ui-input" autocomplete="username" required placeholder="Enter username" />
          </label>
          <label class="auth-field form-field">
            <span class="form-field__label">Class (optional)</span>
            <input v-model="classNote" class="ui-input" placeholder="For example, Class A" />
          </label>
          <div v-if="resetMessage" class="ui-notice" role="status">{{ resetMessage }}</div>
          <button class="btn-primary auth-submit" type="submit" :disabled="resetLoading">
            {{ resetLoading ? 'Sending...' : 'Request password reset' }}
          </button>
          <router-link class="forgot-link" to="/login">Back to login</router-link>
        </form>
      </div>
    </section>
  </main>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import { apiFetch } from '../api'

const emit = defineEmits(['authenticated'])

const username = ref('')
const password = ref('')
const role = ref('student')
const errorMessage = ref('')
const loading = ref(false)
const route = useRoute(), router = useRouter()
const resetMode = computed(() => route.query.help === 'password')
const resetUsername = ref('')
const classNote = ref('')
const resetMessage = ref('')
const resetLoading = ref(false)
const auth = useAuthStore()

async function submitLogin() {
  loading.value = true
  try {
    const user = await auth.login(username.value.trim(), password.value, role.value)
    errorMessage.value = ''
    emit('authenticated', user)
  } catch (error) {
    errorMessage.value = error.message || 'Invalid username or password.'
  } finally {
    loading.value = false
  }
}

function openReset() {
  router.push('/account/reset-password')
}

async function submitResetRequest() {
  resetLoading.value = true
  resetMessage.value = ''
  try {
    const res = await apiFetch('auth/password-reset-requests', {
      method: 'POST',
      body: JSON.stringify({ username: resetUsername.value.trim(), class_note: classNote.value.trim() }),
    })
    const data = await res.json()
    resetMessage.value = data.message || 'If the account can be verified, a teacher or administrator will process the request.'
  } catch {
    resetMessage.value = 'The request could not be sent. Please try again.'
  } finally {
    resetLoading.value = false
  }
}
</script>

<style scoped>
.auth-page {
  min-height: 100vh;
  display: grid;
  place-items: center;
  padding: var(--space-8) var(--space-6);
  background: var(--canvas);
}

.auth-card {
  display: grid;
  grid-template-columns: minmax(0, 0.9fr) minmax(360px, 1.1fr);
  width: min(880px, 100%);
  overflow: hidden;
  border: 1px solid var(--border);
  border-radius: var(--radius-surface);
  background: var(--surface);
}

.auth-brand {
  display: flex;
  min-height: 480px;
  flex-direction: column;
  justify-content: space-between;
  padding: var(--space-12);
  border-right: 1px solid var(--border);
  background: var(--surface-muted);
}

.auth-brand__lockup {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.auth-brand__institution {
  margin-bottom: var(--space-6);
  padding-bottom: var(--space-2);
  border-bottom: 3px solid var(--accent);
  color: var(--accent-strong);
  font-size: 0.8125rem;
  font-weight: 800;
  letter-spacing: 0.16em;
}

.auth-brand__name {
  max-width: 7ch;
  color: var(--ink);
  font-family: var(--font-academic);
  font-size: clamp(2.4rem, 5vw, 3.6rem);
  line-height: 0.98;
}

.auth-brand__descriptor {
  margin-top: var(--space-4);
  color: var(--ink-muted);
  font-size: 0.875rem;
  font-weight: 600;
}

.auth-brand__note {
  max-width: 30ch;
  margin: 0;
  color: var(--ink-muted);
  font-family: var(--font-academic);
  font-size: 1rem;
  line-height: 1.6;
}

.auth-panel {
  align-self: center;
  padding: var(--space-12);
}

.auth-kicker {
  color: var(--accent-strong);
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  margin-bottom: var(--space-3);
  text-transform: uppercase;
}

.auth-panel h1 {
  margin: 0;
  color: var(--ink);
  font-family: var(--font-academic);
  font-size: 2rem;
  line-height: 1.15;
}

.auth-copy {
  margin: var(--space-2) 0 var(--space-6);
  color: var(--ink-muted);
  font-size: 0.875rem;
  line-height: 1.5;
}

.auth-form {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.auth-field {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  margin: 0;
}

.auth-field .form-field__label {
  color: var(--ink);
  font-size: 0.8125rem;
  font-weight: 600;
}

.auth-error {
  border-color: var(--danger);
  color: var(--danger);
  font-size: 0.8125rem;
  line-height: 1.45;
}

.auth-submit {
  width: 100%;
  margin-top: var(--space-1);
}

.forgot-link {
  border: 0;
  padding: var(--space-2);
  background: transparent;
  color: var(--accent-strong);
  cursor: pointer;
  font: inherit;
  font-size: 0.8125rem;
  font-weight: 700;
}

@media (max-width: 767px) {
  .auth-page {
    align-items: start;
    padding: var(--space-4);
  }

  .auth-card {
    grid-template-columns: 1fr;
  }

  .auth-brand {
    min-height: auto;
    padding: var(--space-6);
    border-right: 0;
    border-bottom: 1px solid var(--border);
  }

  .auth-brand__institution {
    margin-bottom: var(--space-3);
  }

  .auth-brand__name {
    max-width: none;
    font-size: 2.25rem;
  }

  .auth-brand__note {
    display: none;
  }

  .auth-panel {
    padding: var(--space-6);
  }

  .auth-field .ui-input,
  .auth-field .ui-select {
    min-height: 44px;
  }
}
</style>
